import { z } from 'zod';
export const billingPlanIdSchema = z.enum(['monthly', 'six_month']);
export const billingPlanSchema = z.object({
    id: billingPlanIdSchema,
    name: z.string(),
    /** Minor units, so 19900 is 199.00 USD. */
    amount: z.number().int().positive(),
    currency: z.literal('usd'),
    /** Recurring interval, or null for a one-time pass. */
    interval: z.enum(['month']).nullable(),
    accessMonths: z.number().int().positive(),
    lookupKey: z.string(),
    summary: z.string(),
});
/** The catalogue the web renders and the Stripe setup script publishes. Keep both in sync here. */
export const BILLING_PLANS = [
    {
        id: 'monthly',
        name: 'Monthly',
        amount: 19900,
        currency: 'usd',
        interval: 'month',
        accessMonths: 1,
        lookupKey: 'mio_residency_monthly',
        summary: 'Renews every month. Cancel any time from the billing portal.',
    },
    {
        id: 'six_month',
        name: '6-month pass',
        amount: 59900,
        currency: 'usd',
        interval: null,
        accessMonths: 6,
        lookupKey: 'mio_residency_six_month',
        summary: 'One payment for six months of access. No renewal.',
    },
];
export function billingPlan(id) {
    const plan = BILLING_PLANS.find((item) => item.id === id);
    if (!plan)
        throw new Error(`Unknown billing plan ${id}`);
    return plan;
}
export const accessStateSchema = z.enum([
    'unlimited',
    'trial',
    'active',
    'grace',
    'expired',
    'suspended',
]);
export const trialSourceSchema = z.enum(['work_email', 'personal_email']);
export const usageResourceSchema = z.enum(['ai_usd', 'voice_seconds']);
/** USD is provider model spend shared by every model-powered surface. */
export const BILLING_ALLOWANCES = {
    personal_trial: { aiUsd: 21, voiceSeconds: 10_800 },
    work_trial: { aiUsd: 30, voiceSeconds: 10_800 },
    paid: { aiUsd: 50, voiceSeconds: 14_400 },
};
export const billingUsageSchema = z.object({
    tier: z.enum(['personal_trial', 'work_trial', 'paid']),
    periodStartsAt: z.iso.datetime(),
    periodEndsAt: z.iso.datetime(),
    /** Paid allowances renew monthly; trial allowances never renew. */
    resetsAt: z.iso.datetime().nullable(),
    ai: z.object({
        limitUsd: z.number().nonnegative(),
        usedUsd: z.number().nonnegative(),
        reservedUsd: z.number().nonnegative(),
        remainingUsd: z.number().nonnegative(),
        exhausted: z.boolean(),
    }),
    voice: z.object({
        limitSeconds: z.number().int().nonnegative(),
        usedSeconds: z.number().int().nonnegative(),
        reservedSeconds: z.number().int().nonnegative(),
        remainingSeconds: z.number().int().nonnegative(),
        exhausted: z.boolean(),
    }),
});
export const billingStatusSchema = z.object({
    enabled: z.boolean(),
    access: z.object({
        state: accessStateSchema,
        /** True when the learner may only see the plan page until they pay. */
        locked: z.boolean(),
        expiresAt: z.iso.datetime().nullable(),
    }),
    trial: z.object({ source: trialSourceSchema, endsAt: z.iso.datetime() }).nullable(),
    subscription: z
        .object({
        plan: billingPlanIdSchema,
        status: z.string(),
        currentPeriodEnd: z.iso.datetime().nullable(),
        cancelAt: z.iso.datetime().nullable(),
    })
        .nullable(),
    pass: z.object({ plan: billingPlanIdSchema, accessUntil: z.iso.datetime() }).nullable(),
    canManageBilling: z.boolean(),
    usage: billingUsageSchema.nullable().default(null),
    plans: z.array(billingPlanSchema),
});
export const createCheckoutSchema = z.object({ plan: billingPlanIdSchema });
export const confirmCheckoutSchema = z.object({ sessionId: z.string().min(1).max(255) });
/** Stripe-hosted pages: the browser navigates to `url`. */
export const billingRedirectSchema = z.object({ url: z.url() });
export const webhookReceiptSchema = z.object({ received: z.literal(true) });
